import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Check, ExternalLink, ImageOff, Loader2, Maximize2,
  Pencil, RefreshCw, Search, Trash2, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * AcademyMediaManager — the shared Founder Portal CMS surface for every
 * Academy media slot (Logic, Physics, Ethics). One component, three pages.
 * Live thumbnails, duplicate detection, alt-text editing, safe replace flow.
 */

const STR = {
  en: {
    intro: "Every fixed image slot of the Academy courses. Assign a hosted image URL and the public page updates immediately; empty slots render a restrained placeholder.",
    filled: "filled", of: "of", refresh: "Refresh", viewCourse: "Open public page",
    search: "Search ID, label, lesson, file name or alt text…",
    allDisciplines: "All disciplines", allPhases: "All phases", allStatuses: "All statuses",
    onlyDuplicates: "Duplicates only", sortId: "Sort: slot ID", sortUpdated: "Sort: last modified",
    status: { active: "Active", draft: "Draft", empty: "Empty", unpublished: "Unpublished", error: "Error" },
    noImage: "No image assigned yet", loadFailed: "Image failed to load",
    file: "File", internalId: "Asset ID", alt: "Alt text", lastModified: "Last modified",
    altMissing: "Alt text missing", phase: "Phase", lesson: "Lesson",
    replace: "Replace image", enlarge: "Enlarge preview", openPublic: "Open public page",
    editAlt: "Edit alt text", remove: "Remove assignment",
    current: "Current", newImage: "New image", urlPlaceholder: "https://… image URL",
    confirmAssign: "Confirm and assign", useAnyway: "Use anyway", chooseOther: "Choose a different image",
    cancel: "Cancel", save: "Save", saving: "Saving…",
    dupWarn: "This image is also used on:",
    assigned: (id) => `Image successfully assigned to ${id}`,
    altSaved: "Alt text saved.", removed: (id) => `Assignment removed from ${id}`,
    removeTitle: "Remove assignment?",
    removeBody: "The image disappears from the public page. The slot itself remains and can be refilled at any time.",
    noResults: "No slots match the current filters.",
    disciplines: { logic: "Logic", physics: "Physics", ethics: "Ethics" },
  },
  nl: {
    intro: "Alle vaste beeldslots van de Academy-leergangen. Koppel een gehoste afbeeldings-URL en de publieke pagina toont het beeld direct; lege slots tonen een ingetogen placeholder.",
    filled: "gevuld", of: "van", refresh: "Vernieuwen", viewCourse: "Open publieke pagina",
    search: "Zoek op ID, label, les, bestandsnaam of alt-tekst…",
    allDisciplines: "Alle disciplines", allPhases: "Alle fasen", allStatuses: "Alle statussen",
    onlyDuplicates: "Alleen dubbelen", sortId: "Sortering: slot-ID", sortUpdated: "Sortering: laatst gewijzigd",
    status: { active: "Actief", draft: "Concept", empty: "Leeg", unpublished: "Niet gepubliceerd", error: "Fout" },
    noImage: "Nog geen afbeelding gekoppeld", loadFailed: "Afbeelding kon niet laden",
    file: "Bestand", internalId: "Asset-ID", alt: "Alt-tekst", lastModified: "Laatst gewijzigd",
    altMissing: "Alt-tekst ontbreekt", phase: "Fase", lesson: "Les",
    replace: "Afbeelding vervangen", enlarge: "Voorbeeld vergroten", openPublic: "Open publieke pagina",
    editAlt: "Alt-tekst bewerken", remove: "Koppeling verwijderen",
    current: "Huidig", newImage: "Nieuw beeld", urlPlaceholder: "https://… afbeeldings-URL",
    confirmAssign: "Bevestig en koppel", useAnyway: "Toch gebruiken", chooseOther: "Kies een ander beeld",
    cancel: "Annuleren", save: "Opslaan", saving: "Opslaan…",
    dupWarn: "Deze afbeelding wordt ook gebruikt op:",
    assigned: (id) => `Afbeelding succesvol gekoppeld aan ${id}`,
    altSaved: "Alt-tekst opgeslagen.", removed: (id) => `Koppeling verwijderd van ${id}`,
    removeTitle: "Koppeling verwijderen?",
    removeBody: "Het beeld verdwijnt van de publieke pagina. Het slot zelf blijft bestaan en kan opnieuw gevuld worden.",
    noResults: "Geen slots gevonden met de huidige filters.",
    disciplines: { logic: "Logica", physics: "Fysica", ethics: "Ethica" },
  },
};

const DISC_TONES = {
  logic: "border-[#5a6f9e]/50 text-[#8fa5d4] bg-[#2e3a50]/30",
  physics: "border-emerald-500/40 text-emerald-300 bg-emerald-900/20",
  ethics: "border-[#c86058]/50 text-[#e0958f] bg-[#6b2a2a]/25",
};

const STATUS_TONES = {
  active: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
  draft: "border-amber-400/50 text-amber-300 bg-amber-400/5",
  empty: "border-white/20 text-bone/50 bg-white/[0.02]",
  unpublished: "border-orange-400/50 text-orange-300 bg-orange-400/5",
  error: "border-red-400/50 text-red-300 bg-red-400/5",
};

const DISC_ORDER = { logic: 0, physics: 1, ethics: 2 };

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "nl" ? "nl-NL" : "en-GB",
      { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function StatusBadge({ status, s }) {
  return (
    <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border ${STATUS_TONES[status] || STATUS_TONES.empty}`}
      data-testid="slot-status-badge">
      {s.status[status] || status}
    </span>
  );
}

function Thumb({ slot, s, onEnlarge, onError, broken }) {
  if (!slot.fileUrl || broken) {
    return (
      <div className="w-full aspect-[3/2] border border-white/10 bg-[#1c2330] flex flex-col items-center justify-center gap-2 px-3 text-center"
        data-testid={`slot-thumb-empty-${slot.discipline}-${slot.assetKey}`}>
        <ImageOff size={18} className="text-bone/25" />
        <span className="text-[10px] tracking-[0.15em] uppercase text-bone/40">
          {broken ? s.loadFailed : s.noImage}
        </span>
      </div>
    );
  }
  return (
    <button type="button" onClick={onEnlarge} aria-label={s.enlarge}
      data-testid={`slot-thumb-${slot.discipline}-${slot.assetKey}`}
      className="group relative w-full aspect-[3/2] border border-white/10 bg-[#1c2330] overflow-hidden cursor-zoom-in">
      <img src={slot.fileUrl} alt={slot.altText || slot.assetKey} loading="lazy"
        onError={onError} className="w-full h-full object-cover" />
      <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/40">
        <Maximize2 size={18} className="text-white/90" />
      </span>
    </button>
  );
}

function DupWarning({ slot, duplicates, s, testid }) {
  const refs = (duplicates[slot.fileUrl] || []).filter(
    (r) => !(r.discipline === slot.discipline && r.assetKey === slot.assetKey));
  if (!slot.fileUrl || refs.length === 0) return null;
  return (
    <div className="mt-2 border border-amber-400/40 bg-amber-400/5 px-3 py-2" data-testid={testid}>
      <p className="flex items-center gap-2 text-[11px] text-amber-300">
        <AlertTriangle size={12} /> {s.dupWarn}
      </p>
      <ul className="mt-1 space-y-0.5">
        {refs.map((r) => (
          <li key={`${r.discipline}-${r.assetKey}`} className="text-[11px] text-amber-200/80 font-mono">
            {r.assetKey} <span className="font-reading text-amber-200/60">· {r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, children, tone = "", testid, disabled, href }) {
  const cls = `flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 text-[10px] tracking-[0.18em] uppercase border transition-colors disabled:opacity-30 ${tone || "border-white/15 text-bone/60 hover:text-gold hover:border-gold/40"}`;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
        <Icon size={13} /> {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testid}>
      <Icon size={13} /> {children}
    </button>
  );
}

function ReplacePanel({ slot, duplicates, allSlots, s, onDone, onCancel }) {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [newBroken, setNewBroken] = useState(false);
  const trimmed = url.trim();
  const dupRefs = useMemo(() => {
    if (!trimmed) return [];
    return allSlots.filter((x) => x.fileUrl === trimmed &&
      !(x.discipline === slot.discipline && x.assetKey === slot.assetKey));
  }, [trimmed, allSlots, slot]);

  const save = async () => {
    if (!trimmed) return;
    setSaving(true); setErr("");
    try {
      await adminApi.patchAcademyMedia(slot.discipline, slot.assetKey, { fileUrl: trimmed });
      onDone(s.assigned(slot.assetKey));
    } catch (e) { setErr(e?.message || String(e)); setSaving(false); }
  };

  return (
    <div className="mt-3 border border-gold/30 bg-black/30 p-4" data-testid={`replace-panel-${slot.discipline}-${slot.assetKey}`}>
      <input value={url} onChange={(e) => { setUrl(e.target.value); setNewBroken(false); }}
        placeholder={s.urlPlaceholder} autoFocus
        data-testid={`replace-url-input-${slot.discipline}-${slot.assetKey}`}
        className="w-full bg-black/40 border border-white/15 text-bone text-[13px] px-3 py-2.5 focus:outline-none focus:border-gold/60" />
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-bone/40 mb-1">{s.current}</p>
          <div className="aspect-[3/2] border border-white/10 bg-[#1c2330] overflow-hidden flex items-center justify-center">
            {slot.fileUrl
              ? <img src={slot.fileUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
              : <span className="text-[10px] text-bone/35 px-2 text-center">{s.noImage}</span>}
          </div>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-bone/40 mb-1">{s.newImage}</p>
          <div className="aspect-[3/2] border border-gold/25 bg-[#1c2330] overflow-hidden flex items-center justify-center">
            {trimmed && !newBroken
              ? <img src={trimmed} alt="" onError={() => setNewBroken(true)} className="w-full h-full object-cover"
                  data-testid={`replace-new-preview-${slot.discipline}-${slot.assetKey}`} />
              : <span className="text-[10px] text-bone/35 px-2 text-center">{newBroken ? s.loadFailed : "—"}</span>}
          </div>
        </div>
      </div>
      {dupRefs.length > 0 && (
        <div className="mt-3 border border-amber-400/40 bg-amber-400/5 px-3 py-2" data-testid={`replace-dup-warning-${slot.discipline}-${slot.assetKey}`}>
          <p className="flex items-center gap-2 text-[11px] text-amber-300"><AlertTriangle size={12} /> {s.dupWarn}</p>
          <ul className="mt-1 space-y-0.5">
            {dupRefs.map((r) => (
              <li key={`${r.discipline}-${r.assetKey}`} className="text-[11px] text-amber-200/80 font-mono">{r.assetKey} <span className="font-reading text-amber-200/60">· {r.label}</span></li>
            ))}
          </ul>
        </div>
      )}
      {err && <p className="mt-2 text-[12px] text-red-300">{err}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={saving || !trimmed || newBroken}
          data-testid={`replace-confirm-${slot.discipline}-${slot.assetKey}`}
          className="flex items-center gap-2 min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-30">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {dupRefs.length > 0 ? s.useAnyway : s.confirmAssign}
        </button>
        {dupRefs.length > 0 && (
          <button type="button" onClick={() => { setUrl(""); setNewBroken(false); }}
            data-testid={`replace-choose-other-${slot.discipline}-${slot.assetKey}`}
            className="min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 hover:text-gold">
            {s.chooseOther}
          </button>
        )}
        <button type="button" onClick={onCancel}
          data-testid={`replace-cancel-${slot.discipline}-${slot.assetKey}`}
          className="min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 hover:text-gold">
          {s.cancel}
        </button>
      </div>
    </div>
  );
}

function AltPanel({ slot, s, onDone, onCancel }) {
  const [alt, setAlt] = useState(slot.altText || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setSaving(true); setErr("");
    try {
      await adminApi.patchAcademyMedia(slot.discipline, slot.assetKey, { altText: alt.trim() });
      onDone(s.altSaved);
    } catch (e) { setErr(e?.message || String(e)); setSaving(false); }
  };
  return (
    <div className="mt-3 border border-gold/30 bg-black/30 p-4" data-testid={`alt-panel-${slot.discipline}-${slot.assetKey}`}>
      <textarea value={alt} onChange={(e) => setAlt(e.target.value)} rows={2} autoFocus
        data-testid={`alt-input-${slot.discipline}-${slot.assetKey}`}
        className="w-full bg-black/40 border border-white/15 text-bone text-[13px] px-3 py-2.5 focus:outline-none focus:border-gold/60 resize-y" />
      {err && <p className="mt-2 text-[12px] text-red-300">{err}</p>}
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={save} disabled={saving}
          data-testid={`alt-save-${slot.discipline}-${slot.assetKey}`}
          className="flex items-center gap-2 min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-30">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} {s.save}
        </button>
        <button type="button" onClick={onCancel}
          className="min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 hover:text-gold">
          {s.cancel}
        </button>
      </div>
    </div>
  );
}

function RemoveConfirm({ slot, s, onDone, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const doRemove = async () => {
    setBusy(true); setErr("");
    try {
      await adminApi.clearAcademyMedia(slot.discipline, slot.assetKey);
      onDone(s.removed(slot.assetKey));
    } catch (e) { setErr(e?.message || String(e)); setBusy(false); }
  };
  return (
    <div className="mt-3 border border-red-400/40 bg-red-400/5 p-4" data-testid={`remove-confirm-${slot.discipline}-${slot.assetKey}`}>
      <p className="text-[13px] text-red-200 font-medium">{s.removeTitle}</p>
      <p className="mt-1 text-[12px] text-bone/60 font-reading">{s.removeBody}</p>
      {err && <p className="mt-2 text-[12px] text-red-300">{err}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={doRemove} disabled={busy}
          data-testid={`remove-yes-${slot.discipline}-${slot.assetKey}`}
          className="flex items-center gap-2 min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-red-400/60 text-red-300 hover:bg-red-400/10 disabled:opacity-30">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} {s.remove}
        </button>
        <button type="button" onClick={onCancel}
          className="min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 hover:text-gold">
          {s.cancel}
        </button>
      </div>
    </div>
  );
}

function SlotCard({ slot, duplicates, allSlots, lang, s, onChanged, onEnlarge }) {
  const [panel, setPanel] = useState(null); // 'replace' | 'alt' | 'remove'
  const [broken, setBroken] = useState(false);
  const [msg, setMsg] = useState("");
  const status = broken && slot.fileUrl ? "error" : slot.status;
  const lessonTitle = slot.lessonTitle ? slot.lessonTitle[lang] || slot.lessonTitle.en : "";
  const phaseTitle = slot.phaseTitle ? slot.phaseTitle[lang] || slot.phaseTitle.en : "";
  const done = (m) => { setPanel(null); setMsg(m); onChanged(); };

  return (
    <div data-testid={`media-slot-card-${slot.discipline}-${slot.assetKey}`}
      className="border border-white/10 bg-white/[0.015] p-4 md:p-5 grid gap-4 grid-cols-1 md:grid-cols-[220px_1fr] items-start">
      <div className="w-full md:w-[220px]">
        <Thumb slot={slot} s={s} broken={broken} onError={() => setBroken(true)}
          onEnlarge={() => onEnlarge(slot)} />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px] text-gold" data-testid={`slot-key-${slot.discipline}-${slot.assetKey}`}>{slot.assetKey}</span>
          <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border ${DISC_TONES[slot.discipline]}`}>
            {s.disciplines[slot.discipline]}
          </span>
          <StatusBadge status={status} s={s} />
          {!slot.altText && slot.fileUrl && (
            <span className="flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-amber-400/50 text-amber-300 bg-amber-400/5"
              data-testid={`alt-missing-${slot.discipline}-${slot.assetKey}`}>
              <AlertTriangle size={10} /> {s.altMissing}
            </span>
          )}
          <span className="text-[11px] text-bone/35">{slot.aspectRatio}</span>
        </div>
        <p className="mt-1.5 text-[13px] text-fog/85 font-reading">{slot.label}</p>
        {(phaseTitle || lessonTitle) && (
          <p className="mt-0.5 text-[12px] text-bone/50 font-reading">
            {phaseTitle && <>{s.phase}: {phaseTitle}</>}
            {phaseTitle && lessonTitle && " · "}
            {lessonTitle && <>{s.lesson}: {lessonTitle}</>}
          </p>
        )}
        <dl className="mt-2 grid gap-x-6 gap-y-0.5 text-[12px] sm:grid-cols-2">
          <div className="flex gap-2 min-w-0">
            <dt className="text-bone/40 shrink-0">{s.file}:</dt>
            <dd className="text-bone/75 font-mono truncate" data-testid={`slot-filename-${slot.discipline}-${slot.assetKey}`}>
              {slot.fileName || "—"}
            </dd>
          </div>
          <div className="flex gap-2 min-w-0">
            <dt className="text-bone/40 shrink-0">{s.internalId}:</dt>
            <dd className="text-bone/75 font-mono truncate">{slot.assetId || "—"}</dd>
          </div>
          <div className="flex gap-2 min-w-0">
            <dt className="text-bone/40 shrink-0">{s.alt}:</dt>
            <dd className="text-bone/75 truncate font-reading" data-testid={`slot-alt-${slot.discipline}-${slot.assetKey}`}>
              {slot.altText || "—"}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-bone/40 shrink-0">{s.lastModified}:</dt>
            <dd className="text-bone/75">{fmtDate(slot.updatedAt, lang)}</dd>
          </div>
        </dl>
        <DupWarning slot={slot} duplicates={duplicates} s={s}
          testid={`dup-warning-${slot.discipline}-${slot.assetKey}`} />
        {msg && <p className="mt-2 text-[12px] text-emerald-300" data-testid={`slot-msg-${slot.discipline}-${slot.assetKey}`}>{msg}</p>}
        <div className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <ActionBtn icon={RefreshCw} onClick={() => { setPanel(panel === "replace" ? null : "replace"); setMsg(""); }}
            testid={`action-replace-${slot.discipline}-${slot.assetKey}`}>{s.replace}</ActionBtn>
          {slot.fileUrl && !broken && (
            <ActionBtn icon={Maximize2} onClick={() => onEnlarge(slot)}
              testid={`action-enlarge-${slot.discipline}-${slot.assetKey}`}>{s.enlarge}</ActionBtn>
          )}
          <ActionBtn icon={ExternalLink} href={slot.publicPath}
            testid={`action-public-${slot.discipline}-${slot.assetKey}`}>{s.openPublic}</ActionBtn>
          <ActionBtn icon={Pencil} onClick={() => { setPanel(panel === "alt" ? null : "alt"); setMsg(""); }}
            testid={`action-alt-${slot.discipline}-${slot.assetKey}`}>{s.editAlt}</ActionBtn>
          {slot.fileUrl && (
            <ActionBtn icon={Trash2} tone="border-red-400/40 text-red-300/80 hover:bg-red-400/10"
              onClick={() => { setPanel(panel === "remove" ? null : "remove"); setMsg(""); }}
              testid={`action-remove-${slot.discipline}-${slot.assetKey}`}>{s.remove}</ActionBtn>
          )}
        </div>
        {panel === "replace" && (
          <ReplacePanel slot={slot} duplicates={duplicates} allSlots={allSlots} s={s}
            onDone={done} onCancel={() => setPanel(null)} />
        )}
        {panel === "alt" && <AltPanel slot={slot} s={s} onDone={done} onCancel={() => setPanel(null)} />}
        {panel === "remove" && <RemoveConfirm slot={slot} s={s} onDone={done} onCancel={() => setPanel(null)} />}
      </div>
    </div>
  );
}

function PreviewModal({ slot, lang, s, onClose }) {
  if (!slot) return null;
  const lessonTitle = slot.lessonTitle ? slot.lessonTitle[lang] || slot.lessonTitle.en : "";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined}
        className="max-w-[96vw] md:max-w-4xl bg-[#141a26] border-white/15 text-bone p-4 md:p-6"
        data-testid="media-preview-modal">
        <DialogTitle className="font-mono text-[14px] text-gold flex flex-wrap items-center gap-2">
          {slot.assetKey}
          <span className="font-reading normal-case tracking-normal text-[13px] text-bone/60">· {slot.label}</span>
        </DialogTitle>
        <div className="flex items-center justify-center bg-black/40 border border-white/10 max-h-[70vh] overflow-hidden">
          <img src={slot.fileUrl} alt={slot.altText || slot.assetKey}
            className="max-w-full max-h-[70vh] object-contain" data-testid="media-preview-image" />
        </div>
        <div className="text-[12px] text-bone/60 space-y-0.5">
          <p><span className="text-bone/40">{s.file}:</span> <span className="font-mono text-bone/80">{slot.fileName || "—"}</span></p>
          <p className="font-reading"><span className="text-bone/40 font-sans">{s.alt}:</span> {slot.altText || "—"}</p>
          {lessonTitle && <p className="font-reading"><span className="text-bone/40 font-sans">{s.lesson}:</span> {lessonTitle}</p>}
        </div>
        <a href={slot.publicPath} target="_blank" rel="noreferrer"
          data-testid="media-preview-public-link"
          className="inline-flex items-center gap-2 self-start min-h-[44px] px-4 py-2 text-[10px] tracking-[0.18em] uppercase border border-gold/50 text-gold hover:bg-gold/10">
          <ExternalLink size={13} /> {s.openPublic}
        </a>
      </DialogContent>
    </Dialog>
  );
}

export default function AcademyMediaManager({ discipline }) {
  const { lang } = useT();
  const s = STR[lang] || STR.en;
  const [slots, setSlots] = useState([]);
  const [duplicates, setDuplicates] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [discFilter, setDiscFilter] = useState(discipline || "all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dupOnly, setDupOnly] = useState(false);
  const [sortMode, setSortMode] = useState("id");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const data = await adminApi.academyMediaOverview();
      setSlots(data.slots || []);
      setDuplicates(data.duplicates || {});
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const phases = useMemo(() => {
    const seen = new Map();
    slots.filter((x) => discFilter === "all" || x.discipline === discFilter)
      .forEach((x) => {
        const t = x.phaseTitle ? x.phaseTitle[lang] || x.phaseTitle.en : "";
        if (t && !seen.has(t)) seen.set(t, t);
      });
    return [...seen.keys()];
  }, [slots, discFilter, lang]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = slots.filter((x) => {
      if (discFilter !== "all" && x.discipline !== discFilter) return false;
      if (statusFilter !== "all" && x.status !== statusFilter) return false;
      if (phaseFilter !== "all") {
        const t = x.phaseTitle ? x.phaseTitle[lang] || x.phaseTitle.en : "";
        if (t !== phaseFilter) return false;
      }
      if (dupOnly && !(x.fileUrl && duplicates[x.fileUrl])) return false;
      if (needle) {
        const lt = x.lessonTitle ? `${x.lessonTitle.en} ${x.lessonTitle.nl}` : "";
        const hay = `${x.assetKey} ${x.label} ${lt} ${x.fileName} ${x.altText}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    list.sort((a, b) => sortMode === "updated"
      ? (b.updatedAt || "").localeCompare(a.updatedAt || "") ||
        a.assetKey.localeCompare(b.assetKey)
      : DISC_ORDER[a.discipline] - DISC_ORDER[b.discipline] ||
        a.assetKey.localeCompare(b.assetKey));
    return list;
  }, [slots, q, discFilter, phaseFilter, statusFilter, dupOnly, sortMode, duplicates, lang, sortMode]);

  const inScope = slots.filter((x) => discFilter === "all" || x.discipline === discFilter);
  const filled = inScope.filter((x) => x.fileUrl).length;
  const selCls = "bg-black/30 border border-white/15 text-bone text-[12px] px-3 py-2.5 min-h-[44px] focus:outline-none focus:border-gold/60";

  return (
    <div className="max-w-5xl" data-testid="academy-media-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[13px] text-fog/80 font-reading max-w-xl">{s.intro}</p>
          <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-bone/40" data-testid="media-filled-count">
            {filled} {s.of} {inScope.length} {s.filled}
          </p>
        </div>
        <button data-testid="media-refresh" onClick={load}
          className="flex items-center gap-2 min-h-[44px] text-[11px] tracking-[0.2em] uppercase text-bone/60 hover:text-gold border border-white/15 px-3 py-2">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {s.refresh}
        </button>
      </div>

      <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <div className="relative sm:col-span-2 lg:col-span-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bone/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={s.search}
            data-testid="media-search"
            className="w-full bg-black/30 border border-white/15 text-bone text-[13px] pl-9 pr-8 py-2.5 min-h-[44px] focus:outline-none focus:border-gold/60" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-bone/40 hover:text-gold p-1" aria-label="clear">
              <X size={14} />
            </button>
          )}
        </div>
        <select value={discFilter} onChange={(e) => { setDiscFilter(e.target.value); setPhaseFilter("all"); }}
          className={selCls} data-testid="media-filter-discipline">
          <option value="all">{s.allDisciplines}</option>
          {["logic", "physics", "ethics"].map((d) => <option key={d} value={d}>{s.disciplines[d]}</option>)}
        </select>
        <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}
          className={selCls} data-testid="media-filter-phase">
          <option value="all">{s.allPhases}</option>
          {phases.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className={selCls} data-testid="media-filter-status">
          <option value="all">{s.allStatuses}</option>
          {["active", "draft", "empty", "unpublished"].map((st) => <option key={st} value={st}>{s.status[st]}</option>)}
        </select>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}
          className={selCls} data-testid="media-sort">
          <option value="id">{s.sortId}</option>
          <option value="updated">{s.sortUpdated}</option>
        </select>
        <label className="flex items-center gap-2 min-h-[44px] px-3 border border-white/15 text-[12px] text-bone/70 cursor-pointer select-none">
          <input type="checkbox" checked={dupOnly} onChange={(e) => setDupOnly(e.target.checked)}
            data-testid="media-filter-duplicates" className="accent-[#c9a227]" />
          {s.onlyDuplicates}
        </label>
      </div>

      {err && <p className="mb-6 text-[13px] text-red-300" data-testid="media-error">{err}</p>}
      {loading && slots.length === 0 && (
        <p className="text-[13px] text-bone/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> …</p>
      )}
      {!loading && visible.length === 0 && slots.length > 0 && (
        <p className="text-[13px] text-bone/50 font-reading" data-testid="media-no-results">{s.noResults}</p>
      )}
      <div className="space-y-4">
        {visible.map((slot) => (
          <SlotCard key={`${slot.discipline}-${slot.assetKey}`} slot={slot}
            duplicates={duplicates} allSlots={slots} lang={lang} s={s}
            onChanged={load} onEnlarge={setPreview} />
        ))}
      </div>
      <PreviewModal slot={preview} lang={lang} s={s} onClose={() => setPreview(null)} />
    </div>
  );
}
